/* ========================================= */
/* src/pages/Login.tsx - PÁGINA DE LOGIN */
/* ========================================= */

import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, LogIn } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/perfil';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const success = await login(formData.email, formData.password);
      if (success) {
        navigate(from, { replace: true });
      } else {
        setError('Email o contraseña incorrectos');
      }
    } catch (err) {
      setError('Error al iniciar sesión. Inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-infinity-black flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-infinity-yellow rounded-full flex items-center justify-center">
              <LogIn size={32} className="text-infinity-black" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-infinity-yellow">Iniciar Sesión</h1>
          <p className="text-infinity-white/80 mt-2">Accede a tu cuenta de InfinityWits</p>
        </div>
        {/* Formulario */}
        <div className="bg-infinity-dark rounded-lg shadow-lg p-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-infinity-yellow mb-2">
                Email
              </label>
              <div className="relative">
                <Mail size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-infinity-yellow" />
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="tu@email.com"
                  className="w-full pl-10 pr-4 py-3 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-infinity-yellow mb-2">
                Contraseña
              </label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-infinity-yellow" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Tu contraseña"
                  className="w-full pl-10 pr-12 py-3 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-infinity-yellow hover:text-infinity-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-infinity-yellow focus:ring-infinity-yellow border-infinity-yellow rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-infinity-white/80">
                  Recordarme
                </label>
              </div>
              <button
                type="button"
                className="text-sm text-infinity-yellow hover:text-infinity-white"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary py-3 text-lg bg-infinity-yellow text-infinity-black hover:bg-infinity-purple hover:text-infinity-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </button>
          </form>
          <div className="mt-6 text-center">
            <p className="text-infinity-white/80">
              ¿No tienes una cuenta?{' '}
              <Link to="/register" className="text-infinity-yellow hover:text-infinity-white font-medium">
                Regístrate aquí
              </Link>
            </p>
          </div>
          {/* Demo credentials */}
          <div className="mt-6 p-4 bg-infinity-gradient rounded-lg">
            <p className="text-sm text-infinity-black font-medium mb-2">Para probar el sistema:</p>
            <p className="text-sm text-infinity-white">Email: demo@infinitywits.com</p>
            <p className="text-sm text-infinity-white">Contraseña: cualquier texto</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;